import createReducer from 'redux/lib/reducerConfig';
import { selectMarketPriceCompositesData } from 'redux/marketPrice/selectors';
import { selectDataCutDisplayModeConfig } from 'redux/user/selectors';
import { showNotification } from 'common/function';
import { reportError } from 'utils/reporting';
import { runCancelableQuery } from 'utils/runCancelableQuery';
import { filterConfigForExport } from 'components/Table/utils';
import { saveAs } from 'file-saver';
import { TagType, CompanyJobSlotJobService, CompositeService, CompositeTagService, PricejobService } from 'types';
import { setCompositesData, updateCompositeData } from '../composites';
import { getCompositesFromNotifications } from '../utils';

export const ActionTypes = {
  UPDATE_COMPOSITE_DATA: 'MARKET_PRICE/UPDATE_COMPOSITE_DATA',
  ADD_TAG: 'MARKET_PRICE/ADD_TAG',
  SET_GROUP_BY_KEY: 'MARKET_PRICE/SET_GROUP_BY_KEY',
  SET_GROUP_BY_VALUE: 'MARKET_PRICE/SET_GROUP_BY_VALUE',
  RESET_TABLE: 'MARKET_PRICE/RESET_TABLE',
  SET_LOADING: 'MARKET_PRICE/SET_LOADING',
  SET_EXPORTING: 'MARKET_PRICE/SET_EXPORTING',
  SET_FILTER_DATA: 'MARKET_PRICE/SET_FILTER_DATA',
};

const setDataLoading = (loading) => ({ type: ActionTypes.SET_LOADING, payload: loading });
export const setDataExporting = (exporting) => ({ type: ActionTypes.SET_EXPORTING, payload: exporting });

const setFilterData = (data) => ({
  type: ActionTypes.SET_FILTER_DATA,
  payload: data,
});

export const setGroupByKey = (key) => ({ type: ActionTypes.SET_GROUP_BY_KEY, payload: key });
export const setGroupByValue = (value) => ({ type: ActionTypes.SET_GROUP_BY_VALUE, payload: value });
export const resetTable = {
  type: ActionTypes.RESET_TABLE,
};

export const fetchMarketPriceData = (filter, pagination, sorting) => async (dispatch) => {
  try {
    const params = { ...filter, ...pagination, sort: sorting };

    dispatch(setDataLoading(true));

    const response = await runCancelableQuery('loadMarketPriceData', () => PricejobService.getPricejobData({
      requestBody: params,
    }));

    if (response) {
      const { rows: data } = response;

      if (!data) {
        dispatch(setDataLoading(false));
        showNotification('', 'Price Job Data Loading Failure', 'error');
        return;
      }

      dispatch(setCompositesData(data));
    }
  } catch (e) {
    showNotification('', 'Price Job Data Loading Failure', 'error');
    reportError(e);
  }
  dispatch(setDataLoading(false));
};

export const reloadMarketPriceComposites = (notifications) => async (dispatch, getState) => {
  try {
    const state = getState();
    const currentComposites = selectMarketPriceCompositesData(state);
    const dataCutDisplayMode = selectDataCutDisplayModeConfig(state);
    const compositesToReload = getCompositesFromNotifications(currentComposites, notifications);

    if (!!compositesToReload.length) {
      const params = {
        compositeIds: compositesToReload,
        dataCutDisplayMode,
      };

      const { rows: data } = await PricejobService.getPricejobData({
        requestBody: params,
      });

      if (!data) {
        showNotification('', 'Price Job Data Loading Failure', 'error');
        return;
      }

      dispatch(updateCompositeData(data));
    }
  } catch (e) {
    reportError(e);
  }
}

export const exportMarketPriceData = (filter, sorting, tableConfig) => async (dispatch) => {
  try {
    const config = filterConfigForExport(tableConfig);

    const params = { ...filter, sort: sorting, config };

    dispatch(setDataExporting(true));

    await PricejobService.exportData({ requestBody: params });
    showNotification('', 'Data Export Started', 'success');

    dispatch(setDataExporting(false));
  } catch (e) {
    reportError(e);
    showNotification(e.message, 'Export error', 'error');
  }
};

export const exportSingleJob = (compositeIds, fileType, dataCutDisplayMode, currencyPlanId) => async (dispatch) => {
  try {
    dispatch(setDataExporting(true));

    const getFile = async (id) => {
      if (fileType === 'xlsx') {
        const blob = await CompositeService.exportCompositeToXlsx({
          compositeId: id,
          dataCutDisplayMode,
          currencyPlanId,
        });
        saveAs(blob, `JobExport_${id}.${fileType}`);
      } else if (fileType === 'pdf') {
        const blob = await CompositeService.exportCompositeToPdf({
          compositeId: id,
          dataCutDisplayMode,
          currencyPlanId,
        });
        saveAs(blob, `JobExport_${id}.${fileType}`);
      }
    };

    const requests = [];
    compositeIds.forEach((id) => {
      requests.push(getFile(id));
    });

    await Promise.all(requests);
    dispatch(setDataExporting(false));
  } catch (e) {
    reportError(e);
  }
  dispatch(setDataExporting(false));
};

export const exportSummaryJobReport = (composites, dataCutDisplayMode, currencyPlanId) => async (dispatch) => {
  try {
    dispatch(setDataExporting(true));

    const blob = await CompositeService.exportToExcel({
      composites,
      dataCutDisplayMode,
      currencyPlanId,
    });
    saveAs(blob, 'JobsSummary.xlsx');

    dispatch(setDataExporting(false));
  } catch (e) {
    reportError(e);
  }
  dispatch(setDataExporting(false));
};

export const fetchFilters = (searchParams) => async (dispatch) => {
  try {
    const filterData = await PricejobService.getFilters({ ...searchParams, tagsType: TagType.COMPOSITE });
    dispatch(setFilterData(filterData));
  } catch (e) {
    reportError(e);
  }
};

export const addMarketPriceTag = ({ compositeIds, tags, replace }) => async () => {
  try {
    await CompositeTagService.assignCompositeTags({
      requestBody: {
        compositeIds,
        tags,
        replace,
      },
    });
  } catch (err) {
    reportError(err);
  }
};

export const editSlotJob = ({ adjustment, weight, id }, onUpdatedSuccessfully) => async () => {
  try {
    await CompanyJobSlotJobService.updateCompanyJobSlotJob({
      companyJobSlotJobId: id,
      requestBody: { adjustment, weight },
    });
    onUpdatedSuccessfully();
  } catch (err) {
    showNotification('Saving Slot Job Error', 'Error', 'error');
    reportError(err);
  }
};

const marketPriceInitialState = {
  effectiveDates: [],
  companyJobsFiltered: [],
  jobFamilies: [],
  jobFamiliesFiltered: [],
  jobLevels: [],
  plans: [],
  paymarkets: [],
  grades: [],
  surveys: [],
  groupByKey: null,
  groupByValue: null,
  loading: false,
  exporting: false,
  age: null,
};

export const tableReducer = createReducer(
  marketPriceInitialState,
  {
    [ActionTypes.SET_LOADING]: (state, { payload }) => {
      return {
        ...state,
        loading: payload,
      };
    },
    [ActionTypes.SET_EXPORTING]: (state, { payload }) => {
      return {
        ...state,
        exporting: payload,
      };
    },
    [ActionTypes.SET_GROUP_BY_KEY]: (state, { payload }) => ({ ...state, groupByKey: payload }),
    [ActionTypes.SET_GROUP_BY_VALUE]: (state, { payload }) => ({
      ...state,
      groupByValue: payload,
      surveys: [],
    }),
    [ActionTypes.RESET_TABLE]: (state) => ({
      ...state,
      age: null,
      groupByKey: null,
      groupByValue: null,
    }),
    [ActionTypes.SET_FILTER_DATA]: (state, { payload }) => ({ ...state, ...payload }),
  }
);
